import React, { useEffect } from 'react';
import '../styles/ProjectDetail.css';
import { useParams, useNavigate } from 'react-router-dom';
import { projectsData } from '../data/projectsData';
import { projectsDetailData } from '../data/projectsData';

const ProjectDetail: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const projectDetail = projectId ? projectsDetailData[projectId] : null;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!projectDetail) {
    return (
      <div style={styles.notFound}>
        <h1 style={styles.notFoundTitle}>Projet non trouvé</h1>
        <button style={styles.backButton} onClick={() => navigate('/')}>
          Retour à l'accueil
        </button>
      </div>
    );
  }

  const isLavoir = projectDetail.id === 'les-femmes-du-lavoir' || projectDetail.id === 'les-lavandieres';

  if (isLavoir) {
    return (
      <div className="project-detail project-detail--les-femmes-du-lavoir">
        <header className="lavoir-nav-header">
          <button 
            className="lavoir-back-button"
            onClick={() => navigate('/')}
          >
            ← Retour
          </button>
        </header>

        <main className="lavoir-main">
          {/* Section Hero / Poster */}
          <section className="lavoir-hero">
            <div className="lavoir-poster-container">
              <img
                src={projectDetail.heroImage || "/assets/les-femmes-du-lavoir/poster.jpg"}
                alt="Les femmes du lavoir - Affiche de la pièce"
                className="lavoir-poster-img"
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                fetchpriority="high"
              />
            </div>
            {projectDetail.heroTagline && (
              <p className="lavoir-tagline">{projectDetail.heroTagline}</p>
            )}
          </section>

          {/* Section L'histoire */}
          <section className="lavoir-section lavoir-section--histoire">
            <h2 className="lavoir-section-heading">L'histoire</h2>
            <div className="lavoir-histoire-grid">
              <div className="lavoir-histoire-text">
                {projectDetail.sections[0]?.content.map((paragraph, idx) => (
                  <p key={idx}>{paragraph}</p>
                ))}
              </div>
              <div className="lavoir-histoire-media">
                <img
                  src={projectDetail.sectionVisuals?.[0]?.[0] || "/assets/les-femmes-du-lavoir/histoire.jpg"}
                  alt="Les femmes du lavoir - Scène"
                  loading="lazy"
                  decoding="async"
                  className="lavoir-histoire-img"
                />
              </div>
            </div>
          </section>

          {/* Section Les inspirations */}
          <section className="lavoir-section lavoir-section--inspirations">
            <h2 className="lavoir-section-heading">Les inspirations</h2>
            <div className="lavoir-inspirations-container">
              <img
                src={projectDetail.lookbook?.src || "/assets/les-femmes-du-lavoir/inspirations.png"}
                alt="Planche d'inspirations"
                loading="lazy"
                decoding="async"
                className="lavoir-inspirations-img"
              />
            </div>
          </section>

          {/* Section Les silhouettes */}
          <section className="lavoir-section lavoir-section--silhouettes">
            <h2 className="lavoir-section-heading">Les silhouettes</h2>
            <div className="lavoir-silhouettes-grid">
              {projectDetail.silhouettes?.items?.map((silh, idx) => (
                <div key={idx} className="lavoir-silhouette-item">
                  <img
                    src={silh}
                    alt={`Silhouette ${idx + 1}`}
                    loading="lazy"
                    decoding="async"
                    className="lavoir-silhouette-img"
                  />
                </div>
              ))}
            </div>
          </section>

          {/* Section Galerie */}
          <section className="lavoir-section lavoir-section--galerie">
            <h2 className="lavoir-section-heading">Galerie</h2>
            <div className="lavoir-gallery-layout">
              {/* Ligne 1 : 3 images */}
              <div className="lavoir-gallery-row lavoir-gallery-row--3">
                <div className="lavoir-gallery-item lavoir-gallery-item--portrait">
                  <img src="/assets/les-femmes-du-lavoir/Galerie/galerie-01.jpg" alt="Scène au lavoir 1" loading="lazy" decoding="async" />
                </div>
                <div className="lavoir-gallery-item lavoir-gallery-item--landscape">
                  <img src="/assets/les-femmes-du-lavoir/Galerie/galerie-02.jpg" alt="Scène à l'accordéon" loading="lazy" decoding="async" />
                </div>
                <div className="lavoir-gallery-item lavoir-gallery-item--portrait">
                  <img src="/assets/les-femmes-du-lavoir/Galerie/galerie-03.jpg" alt="Chant des lavandières" loading="lazy" decoding="async" />
                </div>
              </div>

              {/* Ligne 2 : 2 images */}
              <div className="lavoir-gallery-row lavoir-gallery-row--2">
                <div className="lavoir-gallery-item lavoir-gallery-item--medium">
                  <img src="/assets/les-femmes-du-lavoir/Galerie/galerie-04.jpg" alt="Groupe des lavandières" loading="lazy" decoding="async" />
                </div>
                <div className="lavoir-gallery-item lavoir-gallery-item--wide">
                  <img src="/assets/les-femmes-du-lavoir/Galerie/galerie-05.jpg" alt="Décor du lavoir en plein air" loading="lazy" decoding="async" />
                </div>
              </div>

              {/* Ligne 3 : 2 images */}
              <div className="lavoir-gallery-row lavoir-gallery-row--2-alt">
                <div className="lavoir-gallery-item lavoir-gallery-item--portrait">
                  <img src="/assets/les-femmes-du-lavoir/Galerie/galerie-06.jpg" alt="Lavandière lisant une lettre" loading="lazy" decoding="async" />
                </div>
                <div className="lavoir-gallery-item lavoir-gallery-item--wide">
                  <img src="/assets/les-femmes-du-lavoir/Galerie/galerie-07.jpg" alt="Scène d'ensemble au lavoir" loading="lazy" decoding="async" />
                </div>
              </div>
            </div>
          </section>
        </main>

        {/* Footer / CTA */}
        <footer className="lavoir-footer">
          <h2 className="lavoir-footer-title" onClick={() => navigate('/#projects')}>
            Découvrez d'autres<br />projets
          </h2>
          <button
            className="lavoir-footer-btn"
            onClick={() => navigate('/#projects')}
          >
            Voir tous les projets
          </button>
        </footer>
      </div>
    );
  }

  // Styles dynamiques basés sur le thème du projet
  const dynamicStyles = {
    container: {
      backgroundColor: projectDetail.theme?.backgroundColor || '#FFF',
      color: '#333',
      fontFamily: projectDetail.theme?.fontFamily || "'Montserrat', sans-serif",
    },
    backButton: {
      background: 'none',
      border: `2px solid ${projectDetail.theme?.primaryColor || '#333'}`,
      color: projectDetail.theme?.primaryColor || '#333',
      padding: '0.5rem 1rem',
      borderRadius: '20px',
      cursor: 'pointer',
      fontWeight: 'bold',
      transition: 'all 0.3s ease',
    },
    title: {
      fontFamily: projectDetail.id === 'borrowed-time' ? undefined : (projectDetail.theme?.titleFont || "'Playfair Display', serif"),
      fontSize: '4rem',
      color: projectDetail.theme?.primaryColor || '#333',
      marginBottom: '1rem',
    },
    sectionTitle: {
      fontFamily: projectDetail.id === 'borrowed-time' ? undefined : (projectDetail.theme?.titleFont || "'Playfair Display', serif"),
      fontSize: '2.5rem',
      color: projectDetail.theme?.secondaryColor || '#666',
      textAlign: 'center' as const,
      marginBottom: '2rem',
    },
    sectionAlt: {
      padding: '4rem 5%',
      backgroundColor: projectDetail.theme?.accentColor || '#F5F5F5',
    },
    ctaSection: {
      padding: '5rem 2rem',
      textAlign: 'center' as const,
      backgroundColor: projectDetail.theme?.secondaryColor || '#666',
    },
    ctaTitle: {
      fontFamily: projectDetail.id === 'borrowed-time' ? undefined : (projectDetail.theme?.titleFont || "'Playfair Display', serif"),
      fontSize: '3rem',
      color: projectDetail.theme?.primaryColor || '#0F1B3D',
      marginBottom: '2rem',
    },
    ctaButton: {
      background: projectDetail.theme?.primaryColor || '#333',
      color: projectDetail.id === 'moustik' ? '#000' : '#FFF',
      border: 'none',
      padding: '1rem 2.5rem',
      borderRadius: '30px',
      fontSize: '1.1rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },
  };

  return (
    <div className={`project-detail project-detail--${projectDetail.id} ${projectDetail.id === 'borrowed-time' ? 'borrowed-time' : ''}`} style={dynamicStyles.container}>
      <header style={styles.header}>
        <button 
          style={dynamicStyles.backButton}
          onClick={() => navigate('/')}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = projectDetail.theme?.primaryColor || '#333';
            e.currentTarget.style.color = '#FFF';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = projectDetail.theme?.primaryColor || '#333';
          }}
        >
          ← Retour
        </button>
      </header>

      <main style={styles.mainContent}>
        {/* Section Hero */}
        {projectDetail.heroImage && (
          <section className="hero-image-wrapper" style={styles.heroSection}>
            <img
              src={projectDetail.heroImage}
              alt={`${projectDetail.title} hero`}
              style={styles.heroImage}
            />
          </section>
        )}
        {projectDetail.heroLogo && (
          <section className="hero-image-wrapper" style={{
            ...styles.heroSection,
            backgroundColor: projectDetail.logoConfig?.backgroundColor || projectDetail.theme?.backgroundColor || '#FFF',
            padding: projectDetail.logoConfig?.padding || '50px',
          }}>
            <img
              className="hero-logo"
              src={projectDetail.heroLogo}
              alt={`${projectDetail.title} logo`}
              style={{
                maxWidth: projectDetail.logoConfig?.maxWidth || '60vw',
                maxHeight: projectDetail.logoConfig?.maxHeight || '70vh',
                objectFit: projectDetail.logoConfig?.fit || 'contain',
                width: '100%',
                height: 'auto',
              }}
            />
            {projectDetail.heroBadge && (
              <div style={{
                position: 'absolute',
                top: '20px',
                right: '20px',
                backgroundColor: projectDetail.theme?.primaryColor || '#333',
                color: '#FFF',
                padding: '0.5rem 1rem',
                borderRadius: '20px',
                fontSize: '0.9rem',
                fontWeight: 'bold',
              }}>
                {projectDetail.heroBadge}
              </div>
            )}
            {projectDetail.heroTagline && (
              <p style={{
                textAlign: 'center',
                fontSize: '1.5rem',
                color: projectDetail.theme?.secondaryColor || '#666',
                marginTop: '2rem',
                fontStyle: 'italic',
              }}>
                {projectDetail.heroTagline}
              </p>
            )}
          </section>
        )}

        <section className="project-header" style={styles.projectHeader}>
          <h1 style={dynamicStyles.title}>{projectDetail.title}</h1>
          <p style={styles.subtitle}>{projectDetail.subtitle}</p>
        </section>

        {projectDetail.sections.map((section, index) => {
          // Pour Chemistry: afficher la section 'Mood' mais y intégrer la lookbook
          if (projectDetail.id === 'chemistry-off-white-league-of-legends' && section.title.toLowerCase().includes('mood')) {
            return (
              <section
                key={index}
                style={index % 2 === 0 ? styles.section : dynamicStyles.sectionAlt}
              >
                <h2 style={dynamicStyles.sectionTitle}>{section.title}</h2>
                {section.content.map((paragraph, pIndex) => (
                  <p key={pIndex} style={styles.text}>{paragraph}</p>
                ))}

                {/* Insère la lookbook (Moodboard) ici pour Chemistry */}
                {projectDetail.lookbook && (
                  <div className="lookbook-item" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '1.5rem' }}>
                    <img
                      src={projectDetail.lookbook.src}
                      alt={projectDetail.lookbook.alt}
                      style={{ maxWidth: '100%', height: 'auto', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                    />
                  </div>
                )}
              </section>
            );
          }

          const visuals = projectDetail.sectionVisuals?.[index];

          return (
            <section
              key={index}
              style={index % 2 === 0 ? styles.section : dynamicStyles.sectionAlt}
            >
              <h2 style={dynamicStyles.sectionTitle}>{section.title}</h2>
              
              {/* Layout spécifique pour BORROWED TIME - Objectif et Story telling */}
              {projectDetail.id === 'borrowed-time' && visuals && visuals.length > 0 ? (
                <div className="borrowed-time-layout" style={{
                  display: 'grid',
                  gridTemplateColumns: '2fr 1fr',
                  gap: '3rem',
                  alignItems: 'center',
                  marginTop: '2rem',
                }}>
                  <div className="text-column">
                    {section.content.map((paragraph, pIndex) => (
                      <p key={pIndex} style={{
                        ...styles.text,
                        fontSize: '1.1rem',
                        lineHeight: 1.8,
                        marginBottom: '1.5rem',
                      }}>{paragraph}</p>
                    ))}
                  </div>
                  <div className="image-column" style={{
                    position: 'sticky',
                    top: '120px',
                  }}>
                    <div className="image-container-hover" style={{
                      borderRadius: '12px',
                      overflow: 'hidden',
                      boxShadow: '0 8px 30px rgba(199, 75, 124, 0.15)',
                    }}>
                      <img
                        src={visuals[0]}
                        alt={`${section.title} visual`}
                        loading="lazy"
                        style={{
                          width: '100%',
                          height: 'auto',
                          display: 'block',
                          objectFit: 'cover',
                        }}
                      />
                    </div>
                  </div>
                </div>
              ) : 
              
              /* Layout spécifique pour MOUSTIK */
              projectDetail.id === 'moustik' && visuals && visuals.length > 0 ? (
                  (() => {
                    const isNotreCollection = section.title.trim().toLowerCase() === 'notre collection';
                    let leftImages = isNotreCollection ? [] : visuals.slice(0, 2);
                    let rightImages = isNotreCollection ? visuals : visuals.slice(2, 4);

                    if (isNotreCollection) {
                      const left = visuals.find((img) => img.toLowerCase().includes('texte2.png'));
                      if (left) {
                        leftImages = [left];
                      }
                      const other = visuals.find((img) => img !== left);
                      if (other) {
                        rightImages = [other];
                      } else {
                        rightImages = [];
                      }
                    }

                    return (
                      <div className="moustik-layout" style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 2fr 1fr',
                        gap: '2rem',
                        alignItems: 'start',
                        marginTop: '2rem',
                        position: 'relative',
                      }}>
                        {/* Colonne gauche - images verticales */}
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '1.5rem',
                          position: 'sticky',
                          top: '120px',
                        }}>
                          {leftImages.map((image, imgIndex) => (
                            <div key={imgIndex} className="image-container-hover" style={{
                              borderRadius: '8px',
                              overflow: 'hidden',
                              boxShadow: '0 4px 20px rgba(245, 255, 0, 0.15)',
                            }}>
                              <img
                                src={image}
                                alt={`${section.title} visual ${imgIndex + 1}`}
                                loading="lazy"
                                style={{
                                  width: '100%',
                                  height: 'auto',
                                  display: 'block',
                                  objectFit: 'cover',
                                }}
                              />
                            </div>
                          ))}
                        </div>

                        {/* Colonne centrale - texte */}
                        <div style={{
                          padding: '0 1rem',
                        }}>
                          {section.content.map((paragraph, pIndex) => (
                            <p key={pIndex} style={{
                              ...styles.text,
                              color: projectDetail.theme?.secondaryColor || '#E0E5E3',
                              fontSize: '1.1rem',
                              lineHeight: 1.9,
                              marginBottom: '2rem',
                            }}>{paragraph}</p>
                          ))}
                        </div>

                        {/* Colonne droite - images décoratives */}
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '1.5rem',
                          position: 'sticky',
                          top: '120px',
                        }}>
                          {rightImages.map((image, imgIndex) => (
                            <div key={imgIndex} className="image-container-hover" style={{
                              borderRadius: '8px',
                              overflow: 'hidden',
                              boxShadow: '0 4px 20px rgba(245, 255, 0, 0.15)',
                            }}>
                              <img
                                src={image}
                                alt={`${section.title} visual ${isNotreCollection ? (imgIndex + 1) : (imgIndex + 3)}`}
                                loading="lazy"
                                style={{
                                  width: '100%',
                                  height: 'auto',
                                  display: 'block',
                                  objectFit: 'cover',
                                }}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()
                ) :
                
                /* Layout spécifique LES PERSONNAGES pour Chemistry ou fallback */
                (projectDetail.id === 'chemistry-off-white-league-of-legends' && section.title.trim().toLowerCase() === 'les personnages') ? (
                  <div className="characters-layout" style={{ marginTop: '2rem' }}>
                    <div className="characters-layout__left">
                      <img
                        src="/assets/chemistry-off-white-league-of-legends/visuals/lol-character1.png"
                        alt="Personnage 1"
                        loading="lazy"
                      />
                    </div>
                    <div className="characters-layout__center">
                      {section.content.map((paragraph, pIndex) => (
                        <p key={pIndex} style={styles.text}>{paragraph}</p>
                      ))}
                    </div>
                    <div className="characters-layout__right">
                      <img
                        src="/assets/chemistry-off-white-league-of-legends/visuals/lol-character2.png"
                        alt="Personnage 2"
                        loading="lazy"
                      />
                    </div>
                  </div>
                ) : visuals && visuals.length > 0 ? (
                  (() => {
                    const titleLower = section.title.toLowerCase();
                    const forceStack = titleLower.includes('design') || titleLower.includes('design de la collection');

                    if (forceStack) {
                      return (
                        <div style={{ marginTop: '2rem' }}>
                          {section.content.map((paragraph, pIndex) => (
                            <p key={pIndex} style={styles.text}>{paragraph}</p>
                          ))}
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                            gap: '1rem',
                            marginTop: '1.5rem',
                          }}>
                            {visuals.map((image, imgIndex) => (
                              <div
                                key={imgIndex}
                                className={titleLower.includes('mood') ? 'image-container-hover moodboard-item' : 'image-container-hover'}
                              >
                                <img src={image} alt={`${section.title} visual ${imgIndex + 1}`} loading="lazy" style={{ width: '100%', height: 'auto', objectFit: 'cover', borderRadius: 12 }} />
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div
                        className={`section-visuals-layout ${index === 0 ? 'visuals-left is-first' : (index % 2 === 0 ? 'visuals-right' : 'visuals-left')} ${projectDetail.id === 'chemistry-off-white-league-of-legends' ? 'chemistry' : ''} ${projectDetail.id === 'marinette' && index === 1 ? 'marinette-second' : ''}`}
                        style={{ marginTop: '2rem' }}
                      >
                        <div className="text-column">
                          {section.content.map((paragraph, pIndex) => (
                            <p key={pIndex} style={styles.text}>{paragraph}</p>
                          ))}
                        </div>

                        <div className="visual-column">
                          {visuals.map((image, imgIndex) => (
                            <div
                              key={imgIndex}
                              className={titleLower.includes('mood') ? 'image-container-hover moodboard-item' : 'image-container-hover'}
                            >
                              <img
                                src={image}
                                alt={`${section.title} visual ${imgIndex + 1}`}
                                loading="lazy"
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()
                ) : (
                  section.content.map((paragraph, pIndex) => (
                    <p key={pIndex} style={styles.text}>{paragraph}</p>
                  ))
                )}

              {/* Images additionnelles "après" pour Borrowed Time (moodboard/dessins) */}
              {projectDetail.id === 'borrowed-time' && projectDetail.moodboardAfterImage && section.title.toLowerCase().includes('mood') && (
                <img
                  className="after-image moodboard-after"
                  src={projectDetail.moodboardAfterImage}
                  alt="Moodboard - Borrowed Time"
                  loading="lazy"
                />
              )}
              {projectDetail.id === 'borrowed-time' && projectDetail.dessinsAfterImage && section.title.toLowerCase().includes('dessin') && (
                <img
                  className="after-image dessins-after"
                  src={projectDetail.dessinsAfterImage}
                  alt="Dessins - Borrowed Time"
                  loading="lazy"
                />
              )}
            </section>
          );
        })}

  {projectDetail.lookbook && projectDetail.id !== 'chemistry-off-white-league-of-legends' && (
          <section style={dynamicStyles.sectionAlt}>
            <h2 style={dynamicStyles.sectionTitle}>
                {projectDetail.id === 'chemistry-off-white-league-of-legends' || projectDetail.id === 'moustik' || projectDetail.id === 'borrowed-time' ? 'Moodboard' : 'Croquis'}
              </h2>
            <div className="lookbook-item" style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}>
              <img 
                src={projectDetail.lookbook.src} 
                alt={projectDetail.lookbook.alt} 
                style={{
                  maxWidth: '100%',
                  height: 'auto',
                  borderRadius: '8px',
                  boxShadow: projectDetail.id === 'moustik' 
                    ? '0 4px 20px rgba(245, 255, 0, 0.2)' 
                    : '0 4px 12px rgba(0,0,0,0.1)',
                }}
              />
            </div>
          </section>
        )}

        {projectDetail.moodboardAfterImage && (
          <section style={styles.section}>
            <h2 style={dynamicStyles.sectionTitle}>Dessins</h2>
            <div className="dessins-container" style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '2rem 0',
            }}>
              <img
                src={projectDetail.moodboardAfterImage}
                alt="Dessins de la collection"
                className="image-container-hover"
                style={{
                  maxWidth: '100%',
                  height: 'auto',
                  borderRadius: '12px',
                  boxShadow: projectDetail.id === 'moustik'
                    ? '0 8px 30px rgba(245, 255, 0, 0.25)'
                    : '0 4px 12px rgba(0,0,0,0.1)',
                }}
              />
            </div>

            {/* Image additionnelle "après" pour Borrowed Time - Dessins */}
            {projectDetail.id === 'borrowed-time' && projectDetail.dessinsAfterImage && (
              <img
                className="after-image dessins-after"
                src={projectDetail.dessinsAfterImage}
                alt="Dessins - Borrowed Time"
                loading="lazy"
              />
            )}
          </section>
        )}

        <section id="gallery" style={styles.gallerySection}>
          <h2 style={{ ...dynamicStyles.sectionTitle, color: projectDetail.id === 'moustik' ? '#000' : (projectDetail.theme?.secondaryColor || '#666') }}>Galerie</h2>
          {projectDetail.id === 'borrowed-time' && projectDetail.galleryImages && projectDetail.galleryImages.length > 0 ? (
            <div className="gallery-masonry borrowed-time-gallery">
              {projectDetail.galleryImages.map((url, idx) => (
                <figure key={idx} className="gallery-item">
                  <img src={url} alt={`Galerie Borrowed Time ${idx + 1}`} loading="lazy" />
                </figure>
              ))}
            </div>
          ) : projectDetail.id === 'marinette' ? (
            <div className="gallery-full-width marinette">
              {projectDetail.images && projectDetail.images.map((media, index) => {
                const isVideo = media.endsWith('.mp4') || media.endsWith('.webm');
                
                return (
                  <div
                    key={index}
                    style={{
                      width: '100%',
                      marginBottom: '2rem',
                    }}
                    className="gallery-item image-container-hover"
                  >
                    {isVideo ? (
                      <div className="responsive-media responsive-media--rounded-lg">
                        <video
                          src={media}
                          className="responsive-media__content"
                          controls
                          playsInline
                          preload="metadata"
                        >
                          Votre navigateur ne supporte pas la vidéo.
                        </video>
                      </div>
                    ) : (
                      <img
                        src={media}
                        alt={`${projectDetail.title} - image ${index + 1}`}
                        style={{
                          width: '100%',
                          height: 'auto',
                          display: 'block',
                        }}
                        loading="lazy"
                        decoding="async"
                      />
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className={projectDetail.id === 'moustik' ? 'gallery-grid-moustik' : 'gallery-grid'}>
              {projectDetail.images && projectDetail.images.map((media, index) => {
                const isVideo = media.endsWith('.mp4') || media.endsWith('.webm');
                
                return (
                  <div
                    key={index}
                    style={{
                      ...styles.galleryImageContainer,
                    }}
                    className="gallery-item image-container-hover"
                  >
                    {isVideo ? (
                      <div className="responsive-media responsive-media--rounded-sm">
                        <video
                          src={media}
                          className="responsive-media__content"
                          controls
                          playsInline
                          preload="metadata"
                        >
                          Votre navigateur ne supporte pas la vidéo.
                        </video>
                      </div>
                    ) : (
                      <img
                        src={media}
                        alt={`${projectDetail.title} - image ${index + 1}`}
                        style={styles.galleryImage}
                        loading="lazy"
                        decoding="async"
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </main>

      <footer style={{ ...dynamicStyles.ctaSection, backgroundColor: projectDetail.id === 'moustik' ? '#000' : (projectDetail.theme?.secondaryColor || '#666') }}>
        <h2 style={dynamicStyles.ctaTitle}>Découvrez d'autres projets</h2>
        <button
          className="btn-elegant"
          style={dynamicStyles.ctaButton}
          onClick={() => navigate('/#projects')}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#FFFFFF';
            e.currentTarget.style.color = projectDetail.id === 'moustik' ? '#000' : (projectDetail.theme?.primaryColor || '#333');
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = projectDetail.theme?.primaryColor || '#333';
            e.currentTarget.style.color = projectDetail.id === 'moustik' ? '#000' : '#FFF';
          }}
        >
          Voir tous les projets
        </button>
      </footer>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    backgroundColor: '#FFF',
    color: '#333',
    fontFamily: "'Montserrat', sans-serif",
  },
  header: {
    padding: '1rem 2rem',
    position: 'sticky',
    top: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    backdropFilter: 'blur(10px)',
    zIndex: 1000,
    display: 'flex',
    alignItems: 'center',
  },
  backButton: {
    background: 'none',
    border: '2px solid #333',
    color: '#333',
    padding: '0.5rem 1rem',
    borderRadius: '20px',
    cursor: 'pointer',
    fontWeight: 'bold',
    transition: 'all 0.3s ease',
  },
  heroSection: {
    width: '100%',
    minHeight: '70vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    overflow: 'hidden',
  },
  heroImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  mainContent: {
    padding: '0 2rem',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  projectHeader: {
    textAlign: 'center' as const,
    padding: '4rem 0',
    borderBottom: '1px solid #EEE',
  },
  title: {
    fontFamily: "'Playfair Display', serif",
    fontSize: '4rem',
    color: '#333',
    marginBottom: '1rem',
  },
  subtitle: {
    color: '#999',
    fontSize: '0.9rem',
    marginBottom: '1rem',
    fontStyle: 'italic' as const,
  },
  date: {
    color: '#F5A347',
    fontWeight: 'bold',
    marginBottom: '1rem',
  },
  shortDescription: {
    fontSize: '1.2rem',
    color: '#666',
    fontStyle: 'italic' as const,
  },
  section: {
    padding: '4rem 5%',
  },
  sectionAlt: {
    padding: '4rem 5%',
    backgroundColor: '#FBF7F4',
  },
  sectionTitle: {
    fontFamily: "'Playfair Display', serif",
    fontSize: '2.5rem',
    color: '#B03984',
    textAlign: 'center' as const,
    marginBottom: '2rem',
  },
  text: {
    fontSize: '1.05rem',
    lineHeight: 1.8,
    color: '#333',
    textAlign: 'left' as const,
    marginBottom: '1.5rem',
    maxWidth: '100%',
    margin: '0 0 1.5rem 0',
  },
  gallerySection: {
    padding: '4rem 5%',
    backgroundColor: '#FFFFFF',
  },
  galleryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '1rem',
    maxWidth: '1400px',
    margin: '0 auto',
  },
  galleryImageContainer: {
    overflow: 'hidden',
    borderRadius: '4px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  },
  galleryImage: {
    width: '100%',
    height: 'auto',
    objectFit: 'cover' as const,
    display: 'block',
  },
  ctaSection: {
    padding: '5rem 2rem',
    textAlign: 'center' as const,
    backgroundColor: '#F5A347',
  },
  ctaTitle: {
    fontFamily: "'Playfair Display', serif",
    fontSize: '3rem',
    color: '#FFF',
    marginBottom: '2rem',
  },
  ctaButton: {
    background: '#333',
    color: '#FFF',
    border: 'none',
    padding: '1rem 2.5rem',
    borderRadius: '30px',
    fontSize: '1.1rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },
  notFound: {
    height: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column' as const,
  },
  notFoundTitle: {
    fontSize: '3rem',
    color: '#333',
    fontFamily: "'Playfair Display', serif",
  },
};

export default ProjectDetail;